import type { VolatilityClass } from '../../workers/volatility'
import { coerceCounters, type MonthlyCounters } from '../../workers/counters-format'
import { DRAWERS, DRAWER_LABELS, STAFF_DRAWERS, isDrawer, type Drawer } from '../../workers/drawers'
import {
  coerceDueFacts,
  type DueFact,
  type DueAction,
} from '../../workers/decay-format'
import { toVoiceNoteDraft, toFactRecording, type VoiceNoteDraft, type FactRecording } from '@/lib/voice-notes'
import { env } from '@/lib/env'
import { toFactAttachment, attachedAnswersWarning, type FactAttachment } from '@/lib/fact-files'
import {
  parseAlreadyClosedOutcome,
  parseCaseFileDetail,
  parseCaseFileList,
  type CaseFileDetail,
  type CaseFileOutcome,
  type CaseFileStatus,
  type CaseFileSummary,
} from '@/lib/case-file-shape'

/**
 * Engine client (frontend side).
 *
 * Talks to the Worker's POST /api/chat using the multi-turn contract:
 * sends { pod, messages: [{ role, content }] } and returns { answer, sources }.
 * Provenance (sources) is display-only and never sent back upstream.
 *
 * Phase 3a (multi-tenancy): the request now carries the active pod id so the
 * engine can route to the correct tenant. Today this is the build-time
 * VITE_ACTIVE_POD; in a later phase the engine derives the tenant from the
 * request host / auth session and this field becomes a fallback. The engine
 * validates the id and fails closed (404) on an unknown tenant.
 *
 * Phase 4a (auth): pingAdmin() calls the protected GET /api/admin/ping with a
 * Clerk session token. It is the reference pattern for every future
 * authenticated dashboard call: the caller supplies the token (from Clerk's
 * getToken()); this module sends it as a Bearer header. This module stays
 * React-free — it never calls Clerk hooks itself.
 *
 * Phase 5 (dashboard): the questions client — fetchQuestions / answerQuestion /
 * dismissQuestion — drives the supplier dashboard. Each follows the same authed
 * pattern as pingAdmin (caller supplies the token; we send Bearer), routed
 * through the small private authedRequest() helper so the auth/parse/error
 * handling lives in one place. The engine resolves the tenant from the verified
 * session, so these calls never send a tenant/pod — the server decides whose
 * rows they touch.
 */
export type Role = 'user' | 'assistant'

export interface ChatTurn {
  role: Role
  content: string
}

export interface Source {
  id: string
  page: string
  url: string
  score: number
  /**
   * Kelvo build step 3: the stored original behind this source, which a member
   * of staff can open. Present only on the staff chat, only when the file is
   * held. The Worker re-checks every open; this is a pointer, not a permission.
   */
  documentId?: string
  /**
   * G46: the file's own name. For an answer the owner wrote and attached a file
   * to, `page` is the answer's label and this is the file's; for a fact read
   * from a file they are the same. Absent on older replies — then `page`.
   */
  documentName?: string
}

export interface ChatReply {
  answer: string
  sources: Source[]
}

/** Typed error so the UI can show the engine's message without leaking internals. */
export class ChatError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'ChatError'
  }
}

/**
 * The partner-site mark the embedding page put on this chat's address, if any
 * (csb pod, 9 Oct 2026). The Cornwall Steel Buildings site opens the chat at
 * `?ref=link` or `?ref=referrer` when the visit began on its building
 * partner's site, so a lead taken here is handed back exactly as a form lead
 * would be. Anything else — absent, misspelt, another value — is no mark.
 * Pure: the caller supplies the query string.
 */
export function referralFromSearch(search: string): 'link' | 'referrer' | undefined {
  try {
    const ref = new URLSearchParams(search).get('ref')
    return ref === 'link' || ref === 'referrer' ? ref : undefined
  } catch {
    return undefined
  }
}

export async function sendChat(history: ChatTurn[]): Promise<ChatReply> {
  // Send only role + content per turn — strip anything display-only.
  const messages = history.map(({ role, content }) => ({ role, content }))
  // Only sent when present, so every other embed's request is unchanged. Read
  // through globalThis because this module is also compiled for the Worker,
  // which has no `window` type and no page address.
  const search = (globalThis as { location?: { search?: string } }).location?.search
  const referral = search ? referralFromSearch(search) : undefined
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        referral
          ? { pod: env.VITE_ACTIVE_POD, messages, referral }
          : { pod: env.VITE_ACTIVE_POD, messages },
      ),
    })
  } catch {
    throw new ChatError('No connection to the assistant. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new ChatError('The assistant returned an unreadable response. Please try again.', res.status)
  }
  if (!res.ok) {
    const message =
      (data as { message?: string } | null)?.message ??
      'The assistant is temporarily unavailable. Please try again.'
    throw new ChatError(message, res.status)
  }
  const reply = (data ?? {}) as Partial<ChatReply>
  return {
    answer: typeof reply.answer === 'string' ? reply.answer : '',
    sources: Array.isArray(reply.sources) ? reply.sources : [],
  }
}

/**
 * THE STAFF CHAT (Kelvo build step 2, item 5) — POST /api/staff/chat.
 *
 * SAME REPLY SHAPE AS sendChat, so the one chat screen serves both. Two
 * differences, and both are the security model showing through:
 *
 *  - NO POD IN THE BODY. The Worker takes the pod from the signed-in session
 *    and REFUSES a body that names one (400). Sending VITE_ACTIVE_POD here, as
 *    the public chat does, would break every request — by design.
 *  - A SESSION TOKEN, fetched fresh per request by the caller (Clerk tokens
 *    are short-lived; a cached one expires mid-conversation).
 *
 * Errors are worded for a member of staff, and 401 and 403 are told apart
 * because they need different actions: 401 means sign in again, 403 means
 * this account has not been given access — something only the office can fix.
 */
export async function sendStaffChat(history: ChatTurn[], token: string | null): Promise<ChatReply> {
  if (!token) {
    throw new ChatError('Your sign-in has expired. Please sign in again.', 401)
  }
  const messages = history.map(({ role, content }) => ({ role, content }))
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}/api/staff/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ messages }),
    })
  } catch {
    throw new ChatError('No connection. Check your signal and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new ChatError('The assistant returned an unreadable response. Please try again.', res.status)
  }
  if (res.status === 401) {
    throw new ChatError('Your sign-in has expired. Please sign in again.', 401)
  }
  if (res.status === 403) {
    // The Worker's own wording ("does not have access" / "not active"), which
    // is already written for staff; fall back only if it is missing.
    const message =
      (data as { message?: string } | null)?.message ??
      'This account does not have access to the office assistant. Please speak to the office.'
    throw new ChatError(message, 403)
  }
  if (!res.ok) {
    const message =
      (data as { message?: string } | null)?.message ??
      'The office assistant is temporarily unavailable. Please try again.'
    throw new ChatError(message, res.status)
  }
  const reply = (data ?? {}) as Partial<ChatReply>
  return {
    answer: typeof reply.answer === 'string' ? reply.answer : '',
    sources: Array.isArray(reply.sources) ? reply.sources : [],
  }
}

/** Result of the protected admin probe. */
export interface AdminPingResult {
  ok: true
  userId: string
}

/** Typed error for the authenticated admin check, mirroring ChatError. */
export class AuthCheckError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'AuthCheckError'
  }
}

/**
 * Call the protected GET /api/admin/ping with a Clerk session token.
 *
 * The caller passes the token (from Clerk's getToken()); a null/empty token is
 * treated as not-signed-in. On the wire the backend verifies the token and
 * checks authorizedParties — so a missing or invalid token returns 401, which
 * surfaces here as an AuthCheckError.
 */
export async function pingAdmin(token: string | null): Promise<AdminPingResult> {
  if (!token) {
    throw new AuthCheckError('No active session. Please sign in again.', 401)
  }
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}/api/admin/ping`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch {
    throw new AuthCheckError('No connection to the engine. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new AuthCheckError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message =
      (data as { message?: string } | null)?.message ?? 'Authorization check failed.'
    throw new AuthCheckError(message, res.status)
  }
  const result = (data ?? {}) as Partial<AdminPingResult>
  if (result.ok !== true || typeof result.userId !== 'string') {
    throw new AuthCheckError('Unexpected response from the engine.', res.status)
  }
  return { ok: true, userId: result.userId }
}

// ---------------------------------------------------------------------------
// Phase 5 — supplier dashboard questions client.
//
// The volatility vocabulary is imported from the engine's dependency-free
// module rather than restated here. Restating it is how a UI ends up offering a
// class the engine does not accept, or wording that disagrees with what the
// re-confirmation message later says.
// ---------------------------------------------------------------------------

/** The two tabs the dashboard reads. (The store also has a 'dismissed' state,
 *  but the UI never lists binned rows.) */
export type QuestionStatus = 'pending' | 'answered'

/** A captured question as the dashboard reads it. Mirrors the engine's row,
 *  minus tenant_id (the server scopes by the session tenant; the client never
 *  needs it). Times are unix-epoch seconds; answer_text/answered_at are null
 *  until the supplier answers. */
export interface Question {
  id: string
  question_text: string
  status: 'pending' | 'answered' | 'dismissed'
  answer_text: string | null
  created_at: number
  answered_at: number | null
  /**
   * Kelvo part 2c: where this answer is filed NOW, on an answered question on
   * a drawers pod. null means no fact stands behind the answer. undefined
   * means the engine sent no `filed` key at all — Foredesk, and every pending
   * question — and the card says nothing about drawers.
   */
  filed?: AnsweredFiling | null
  /**
   * Build 10b: the voice draft waiting on a pending question, on a pod with
   * voice answers. null means none; undefined means the engine sent no key —
   * every other pod — and the card offers no voice answer.
   */
  voiceDraft?: VoiceNoteDraft | null
}

/** Where an answered question's answer is filed now (see workers/fact-filing.ts). */
export interface AnsweredFiling {
  /** The fact to find it by on the Facts page. */
  factId: string
  /** null is unfiled: only the owner is told it. */
  drawer: Drawer | null
  /** false when he has taken it out: nobody is told it any more. */
  live: boolean
}

/**
 * Read one `filed` value off the wire. Anything malformed reads as null — "no
 * fact stands behind this answer" — the reading that claims the least. A
 * drawer the vocabulary does not know reads as unfiled; a `live` that is not
 * exactly true reads as taken out. Both understate who is told, never
 * overstate it.
 */
function toAnsweredFiling(raw: unknown): AnsweredFiling | null {
  if (raw === null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (typeof r.factId !== 'string' || !r.factId) return null
  return { factId: r.factId, drawer: isDrawer(r.drawer) ? r.drawer : null, live: r.live === true }
}

/** Typed error for the questions client, mirroring the others above. */
export class QuestionsError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'QuestionsError'
  }
}

/**
 * Shared authed request for the admin questions endpoints. Caller supplies the
 * Clerk token; a null/empty token is treated as not-signed-in (mirrors
 * pingAdmin). Sends the Bearer header, parses JSON, and maps a non-2xx into a
 * QuestionsError carrying the engine's message. Returns the parsed body for the
 * caller to validate. React-free, like the rest of this module.
 */
async function authedRequest(
  token: string | null,
  path: string,
  init: { method: string; headers?: Record<string, string>; body?: string },
): Promise<unknown> {
  if (!token) {
    throw new QuestionsError('No active session. Please sign in again.', 401)
  }
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method: init.method,
      headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` },
      body: init.body,
    })
  } catch {
    throw new QuestionsError('No connection to the engine. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new QuestionsError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: string } | null)?.message ?? 'Request failed.'
    throw new QuestionsError(message, res.status)
  }
  return data
}

/** Defensive coercion of one raw row into a Question (never trust the wire). */
function toQuestion(raw: unknown): Question {
  const r = (raw ?? {}) as Record<string, unknown>
  const status =
    r.status === 'answered' ? 'answered' : r.status === 'dismissed' ? 'dismissed' : 'pending'
  return {
    id: typeof r.id === 'string' ? r.id : '',
    question_text: typeof r.question_text === 'string' ? r.question_text : '',
    status,
    answer_text: typeof r.answer_text === 'string' ? r.answer_text : null,
    created_at: typeof r.created_at === 'number' ? r.created_at : 0,
    answered_at: typeof r.answered_at === 'number' ? r.answered_at : null,
    // Present only when the engine sent the key, so Foredesk's objects are
    // exactly what they always were.
    ...('filed' in r ? { filed: toAnsweredFiling(r.filed) } : {}),
    ...('voiceDraft' in r ? { voiceDraft: toVoiceNoteDraft(r.voiceDraft) } : {}),
  }
}

/** List the signed-in supplier's questions for one tab. The engine scopes the
 *  query to the session tenant; we send only the status filter. */
export async function fetchQuestions(
  token: string | null,
  status: QuestionStatus,
): Promise<Question[]> {
  const data = await authedRequest(
    token,
    `/api/admin/questions?status=${encodeURIComponent(status)}`,
    { method: 'GET' },
  )
  const list = (data as { questions?: unknown } | null)?.questions
  if (!Array.isArray(list)) {
    throw new QuestionsError('Unexpected response from the engine.')
  }
  return list.map(toQuestion)
}

/**
 * Answer a pending question. Resolves on success; throws QuestionsError on
 * failure (including 404 if the row is no longer a pending row for this tenant).
 *
 * `volatilityClass` is how often the owner says this answer changes — it decides
 * when the engine will ask him to re-confirm it. Sent EXPLICITLY: the engine
 * will propose one if it is omitted, but a proposal the owner never saw is a
 * silent default, and a price quietly classed as long-lived answers stale for a
 * year with nothing to flag it. The dashboard shows the proposal and sends what
 * the owner actually confirmed.
 *
 * `certExpiresAt` (unix epoch seconds) is REQUIRED when the class is
 * 'cert_tied': a certificate's own date is its expiry, and the engine has no way
 * to derive one.
 */
export async function answerQuestion(
  token: string | null,
  id: string,
  answer: string,
  volatilityClass: VolatilityClass,
  certExpiresAt?: number,
  /** Kelvo only: the drawer to file the answer in. Absent on Foredesk. */
  drawer?: Drawer,
  /**
   * Build 10b: the voice draft these words were spoken as. The engine mints
   * the fact from `answer` — his words — and only keeps the recording against
   * it. Absent for a typed answer.
   */
  voiceNoteId?: string,
): Promise<void> {
  await authedRequest(token, `/api/admin/questions/${encodeURIComponent(id)}/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `drawer` and `voiceNoteId` are left out entirely when absent, so
    // Foredesk sends exactly the body it always has (JSON.stringify drops an
    // undefined key either way; this states it rather than relying on it).
    body: JSON.stringify({
      answer,
      volatilityClass,
      certExpiresAt,
      ...(drawer ? { drawer } : {}),
      ...(voiceNoteId ? { voiceNoteId } : {}),
    }),
  })
}

/** Dismiss a pending question (bin junk). Same contract as answerQuestion. */
export async function dismissQuestion(token: string | null, id: string): Promise<void> {
  await authedRequest(token, `/api/admin/questions/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST',
  })
}

/** What the engine returns when a voice capture is minted for a question. */
export interface VoiceLinkResult {
  captureId: string
  /** The raw capture token. The record URL is `${origin}/record/${token}`. */
  token: string
  /** Whether the link was successfully texted to the owner's phone. */
  smsSent: boolean
}

/**
 * Mint a voice capture for a pending question and text the owner the link
 * (Build 1). The engine sends the SMS itself; this returns the token as well so
 * the dashboard can show the link when the text does not arrive.
 *
 * WHY THE TOKEN COMES BACK AT ALL. The owner is, by premise, in a van — the text
 * IS the product. But a Twilio failure must not leave him with a question he
 * cannot answer by voice, so the response carries the fallback. The engine's own
 * comment records the same reasoning from the other side: the raw token travels
 * exactly twice, in this response and in the SMS body, and is never logged.
 *
 * NOTE FOR THE READER WHO WONDERS WHY THIS EXISTS SO LATE. The endpoint has been
 * live and tested since Build 1; nothing in the dashboard ever called it. Voice
 * capture — the thing the product leads on — had no front door on this dashboard
 * until 3 Sep 2026, and the Voice drafts tab invited the owner to review
 * recordings he had no way to make.
 */
export async function requestVoiceLink(
  token: string | null,
  id: string,
): Promise<VoiceLinkResult> {
  const body = await authedRequest(
    token,
    `/api/admin/questions/${encodeURIComponent(id)}/voice-link`,
    { method: 'POST' },
  )
  const r = (body ?? {}) as Record<string, unknown>
  const captureId = asString(r.captureId)
  const captureToken = asString(r.token)
  if (!captureId || !captureToken) {
    throw new QuestionsError('The link could not be created. Try again.')
  }
  return { captureId, token: captureToken, smsSent: r.smsSent === true }
}

// ---------------------------------------------------------------------------
// Phase 5 — supplier dashboard leads client. Same authed pattern as the
// questions client (caller supplies the Clerk token; we send Bearer; the engine
// resolves the tenant from the verified session, so these calls never send a
// tenant). Two endpoints: list the tenant's leads (optionally by pipeline
// state) and move a lead's state.
// ---------------------------------------------------------------------------

/** The lead pipeline vocabulary, pinned to the engine's CHECK. Transitions are
 *  free-form (any state to any state) — the UI offers all six. */
export type LeadStatus = 'new' | 'contacted' | 'quoted' | 'won' | 'lost' | 'dead'

/** A lead as the dashboard LISTS it — the engine's list DTO: the row minus the
 *  transcript (detail-only, deferred) and tenant_id (server-scoped). Every empty
 *  metadata field is '' not null; estimated_value is a human-readable range
 *  string, never a number. Times are unix-epoch seconds. */
export interface Lead {
  id: string
  customer_name: string
  customer_phone: string
  job_type: string
  property_type: string
  estimated_duration: string
  estimated_value: string
  callback_time: string
  lead_score: 'hot' | 'warm' | 'cold'
  area: string
  suggested_opener: string
  status: LeadStatus
  created_at: number
  updated_at: number
}

/** Typed error for the leads client, mirroring QuestionsError. */
export class LeadsError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'LeadsError'
  }
}

/** Shared authed request for the admin leads endpoints (mirrors the questions
 *  client's helper, with a LeadsError). */
async function leadsRequest(
  token: string | null,
  path: string,
  init: { method: string; headers?: Record<string, string>; body?: string },
): Promise<unknown> {
  if (!token) {
    throw new LeadsError('No active session. Please sign in again.', 401)
  }
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method: init.method,
      headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` },
      body: init.body,
    })
  } catch {
    throw new LeadsError('No connection to the engine. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new LeadsError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: string } | null)?.message ?? 'Request failed.'
    throw new LeadsError(message, res.status)
  }
  return data
}

const LEAD_SCORES = ['hot', 'warm', 'cold'] as const
const LEAD_STATUSES = ['new', 'contacted', 'quoted', 'won', 'lost', 'dead'] as const

/** Coerce a raw string field, defaulting to ''. */
function asString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}
/** Coerce a raw numeric field, defaulting to 0. */
function asNumber(v: unknown): number {
  return typeof v === 'number' ? v : 0
}

/** Defensive coercion of one raw row into a Lead (never trust the wire). An
 *  out-of-vocabulary score/status is coerced to the safe middle/default rather
 *  than trusted, so a malformed row can never render an invalid control value. */
function toLead(raw: unknown): Lead {
  const r = (raw ?? {}) as Record<string, unknown>
  const lead_score = (LEAD_SCORES as readonly string[]).includes(r.lead_score as string)
    ? (r.lead_score as 'hot' | 'warm' | 'cold')
    : 'warm'
  const status = (LEAD_STATUSES as readonly string[]).includes(r.status as string)
    ? (r.status as LeadStatus)
    : 'new'
  return {
    id: asString(r.id),
    customer_name: asString(r.customer_name),
    customer_phone: asString(r.customer_phone),
    job_type: asString(r.job_type),
    property_type: asString(r.property_type),
    estimated_duration: asString(r.estimated_duration),
    estimated_value: asString(r.estimated_value),
    callback_time: asString(r.callback_time),
    lead_score,
    area: asString(r.area),
    suggested_opener: asString(r.suggested_opener),
    status,
    created_at: asNumber(r.created_at),
    updated_at: asNumber(r.updated_at),
  }
}

/** List the signed-in supplier's leads. `status` optional: omitted returns every
 *  lead, newest first; supplied, filters to one state. The engine scopes the
 *  query to the session tenant — we never send a tenant. */
export async function fetchLeads(token: string | null, status?: LeadStatus): Promise<Lead[]> {
  const path = status ? `/api/admin/leads?status=${encodeURIComponent(status)}` : '/api/admin/leads'
  const data = await leadsRequest(token, path, { method: 'GET' })
  const list = (data as { leads?: unknown } | null)?.leads
  if (!Array.isArray(list)) {
    throw new LeadsError('Unexpected response from the engine.')
  }
  return list.map(toLead)
}

/** Move a lead to a new pipeline state. Resolves on success; throws LeadsError
 *  on failure (including 404 if the lead is not this tenant's). Leaving `new`
 *  cancels the lead's scheduled follow-up chase — enforced server-side. */
export async function updateLeadStatus(
  token: string | null,
  id: string,
  status: LeadStatus,
): Promise<void> {
  await leadsRequest(token, `/api/admin/leads/${encodeURIComponent(id)}/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
}

/** Erase a lead and its scheduled follow-ups. Permanent. Resolves on success;
 *  throws LeadsError on failure (including 404 if the lead is not this tenant's).
 *  This is how a supplier honours an erasure request or bins a test/spam lead. */
export async function deleteLead(token: string | null, id: string): Promise<void> {
  await leadsRequest(token, `/api/admin/leads/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

/** Everything held about one enquiry, as a file for the person who asked
 *  (Build 13f): their subject access request, answered from the card. Throws
 *  LeadsError on failure, including 404 if the lead is not this tenant's. */
export async function exportLead(token: string | null, id: string): Promise<Blob> {
  if (!token) throw new LeadsError('No active session. Please sign in again.', 401)
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}/api/admin/leads/${encodeURIComponent(id)}/export`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch {
    throw new LeadsError('No connection to the engine. Check your network and try again.')
  }
  if (!res.ok) {
    let message = 'Could not export this enquiry.'
    try {
      message = ((await res.json()) as { message?: string }).message ?? message
    } catch {
      // keep the plain message
    }
    throw new LeadsError(message, res.status)
  }
  return await res.blob()
}

// ---------------------------------------------------------------------------
// Phase 6 — supplier billing client. Same authed pattern as the questions
// client: caller supplies the Clerk token; we send Bearer; the engine resolves
// the tenant from the verified session, so these calls never send a tenant.
// ---------------------------------------------------------------------------

/** The signed-in supplier's billing status, as the dashboard reads it. `entitled`
 *  is the gate's source of truth; `status` distinguishes "never subscribed"
 *  (null) from a lapsed state (e.g. 'past_due'); currentPeriodEnd is epoch secs. */
export interface BillingStatus {
  entitled: boolean
  status: string | null
  currentPeriodEnd: number | null
  /**
   * 'operator' when this pod is the operator's own product — entitled by
   * declaration on the pod contract, with no Stripe subscription behind it.
   * Sent EXPLICITLY by the engine rather than inferred here: entitled-with-
   * null-status already means something else (a fresh trial mid-webhook), and
   * two different states must not render the same.
   */
  reason?: 'operator'
}

/** Typed error for the billing client, mirroring QuestionsError. */
export class BillingError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'BillingError'
  }
}

/** Shared authed request for the billing endpoints (mirrors the questions
 *  client's helper, with a BillingError). */
async function billingRequest(
  token: string | null,
  path: string,
  init: { method: string },
): Promise<unknown> {
  if (!token) {
    throw new BillingError('No active session. Please sign in again.', 401)
  }
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method: init.method,
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch {
    throw new BillingError('No connection to the engine. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new BillingError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: string } | null)?.message ?? 'Request failed.'
    throw new BillingError(message, res.status)
  }
  return data
}

/** Read the signed-in supplier's billing status. */
export async function fetchBillingStatus(token: string | null): Promise<BillingStatus> {
  const data = await billingRequest(token, '/api/admin/billing/status', { method: 'GET' })
  const r = (data ?? {}) as Record<string, unknown>
  return {
    entitled: r.entitled === true,
    status: typeof r.status === 'string' ? r.status : null,
    currentPeriodEnd: typeof r.currentPeriodEnd === 'number' ? r.currentPeriodEnd : null,
    ...(r.reason === 'operator' ? { reason: 'operator' as const } : {}),
  }
}

/** Start a Stripe Checkout session and return its hosted URL. The caller
 *  redirects the supplier there to subscribe. */
export async function startCheckout(token: string | null): Promise<string> {
  const data = await billingRequest(token, '/api/admin/billing/checkout', { method: 'POST' })
  const url = (data as { url?: unknown } | null)?.url
  if (typeof url !== 'string' || !url) {
    throw new BillingError('Could not start checkout. Please try again.')
  }
  return url
}

/** Open the Stripe Customer Portal and return its hosted URL. The caller
 *  redirects the supplier there to manage their card / cancel. */
export async function openBillingPortal(token: string | null): Promise<string> {
  const data = await billingRequest(token, '/api/admin/billing/portal', { method: 'POST' })
  const url = (data as { url?: unknown } | null)?.url
  if (typeof url !== 'string' || !url) {
    throw new BillingError('Could not open billing management. Please try again.')
  }
  return url
}

// ---------------------------------------------------------------------------
// Counters client (Build 1 remainder) — the owner's four monthly numbers.
//
// Same authed pattern as the rest: the caller supplies the Clerk token, the
// engine resolves the tenant from the verified session, so this call never
// sends a tenant. GET-only; the numbers are derived at read time and there is
// nothing to mutate.
//
// The SHAPE and the WORDING both come from workers/counters-format.ts — the
// engine's own dependency-free module — rather than being restated here. The
// rules that turn these counters into words (a null rate is "no traffic", not
// 0%; a counter whose emitter has not shipped never renders a number) are
// exactly the kind that a second copy gets subtly wrong, and getting them wrong
// means telling the owner his product did something it did not do.
// ---------------------------------------------------------------------------

/** Typed error for the counters client, mirroring the others above. */
export class CountersError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'CountersError'
  }
}

/** Shared authed request for the counters endpoint (mirrors billingRequest). */
async function countersRequest(token: string | null, path: string): Promise<unknown> {
  if (!token) {
    throw new CountersError('No active session. Please sign in again.', 401)
  }
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch {
    throw new CountersError('No connection to the engine. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new CountersError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: string } | null)?.message ?? 'Request failed.'
    throw new CountersError(message, res.status)
  }
  return data
}

/**
 * Read the signed-in supplier's counters. Defaults to the current UTC calendar
 * month (the engine's default window); an explicit window is unix epoch seconds,
 * from-inclusive and to-exclusive, matching the engine's contract.
 *
 * A response WITHOUT a counters object THROWS rather than being coerced. An
 * empty report and a broken response would otherwise render identically — as a
 * quiet "no traffic yet" — and the owner would read a fault as a quiet month.
 */
export async function fetchCounters(
  token: string | null,
  window?: { fromSeconds: number; toSeconds: number },
): Promise<MonthlyCounters> {
  const query = window ? `?from=${window.fromSeconds}&to=${window.toSeconds}` : ''
  const data = await countersRequest(token, `/api/admin/counters${query}`)
  const raw = (data as { counters?: unknown } | null)?.counters
  if (raw === null || typeof raw !== 'object') {
    throw new CountersError('Unexpected response from the engine.')
  }
  return coerceCounters(raw)
}

/**
 * The counters' shape and their owner-facing wording, RE-EXPORTED from the
 * engine's dependency-free module for the same reason the volatility vocabulary
 * is: one definition, imported by both lanes, so the dashboard cannot drift into
 * saying something the engine's own numbers do not support.
 */
export type { MonthlyCounters }
export {
  summariseCounters,
  formatWindowLabel,
  NO_NUMBER,
} from '../../workers/counters-format'
export type { CounterTile } from '../../workers/counters-format'

// ---------------------------------------------------------------------------
// Re-confirmation queue client (Build 3a) — what has gone out of date, and the
// two verbs that resolve it.
//
// TWO VERBS, NOT ONE. Confirming a certificate-tied fact deliberately does not
// move its expiry, so the engine REFUSES a confirm on one (409) rather than
// succeeding uselessly and chasing the owner again next week. The client mirrors
// that: a 'renew' row is never offered a Confirm button. The shape and the
// wording both come from workers/decay-format.ts so the two lanes cannot drift
// into disagreeing about which rows get which verb.
// ---------------------------------------------------------------------------

/** Typed error for the re-confirmation client. */
export class DecayError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'DecayError'
  }
}

async function decayRequest(
  token: string | null,
  path: string,
  init: { method: string; body?: unknown },
): Promise<unknown> {
  if (!token) {
    throw new DecayError('No active session. Please sign in again.', 401)
  }
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method: init.method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    })
  } catch {
    throw new DecayError('No connection to the engine. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new DecayError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: string } | null)?.message ?? 'Request failed.'
    throw new DecayError(message, res.status)
  }
  return data
}

/** Everything of this supplier's that has gone out of date, soonest first. */
export async function fetchDueFacts(token: string | null): Promise<DueFact[]> {
  const data = await decayRequest(token, '/api/admin/facts/due', { method: 'GET' })
  return coerceDueFacts((data as { facts?: unknown } | null)?.facts)
}

/** "Still true" — restarts the clock. Refused by the engine for certificates. */
export async function confirmDueFact(token: string | null, id: string): Promise<void> {
  await decayRequest(token, `/api/admin/facts/${id}/confirm`, { method: 'POST' })
}

/**
 * A new certificate date. `certExpiresAt` is epoch seconds — build it with
 * certDateToEpoch, never with `new Date(value).getTime()`, which would record
 * the start of the day and expire a live certificate a day early.
 */
export async function renewDueFact(
  token: string | null,
  id: string,
  certExpiresAt: number,
): Promise<void> {
  await decayRequest(token, `/api/admin/facts/${id}/renew`, {
    method: 'POST',
    body: { certExpiresAt },
  })
}

/** Shape, wording and date handling, re-exported from the engine's own module. */
export type { DueFact, DueAction }
export {
  certDateToEpoch,
  epochToCertDate,
  describeOverdue,
  describeQueue,
  RENEW_EXPLANATION,
} from '../../workers/decay-format'

// ---------------------------------------------------------------------------
// Document ingestion + the fact review queue (Build 2a) — paste paperwork,
// approve what came out.
//
// Same authed pattern as every other client here. The engine's rules do the
// real work (evidence veto, duplicate suppression, cert-date requirement); this
// client's one responsibility beyond transport is HONEST WORDING of the result:
// the ingest response distinguishes five outcomes (proposed / duplicate / needs
// a certificate date / vetoed / failed chunks) precisely so the owner is never
// shown a short list that looks complete.
// ---------------------------------------------------------------------------

export class DocumentsError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'DocumentsError'
  }
}

async function documentsRequest(
  token: string | null,
  path: string,
  init: { method: string; body?: unknown },
): Promise<unknown> {
  if (!token) {
    throw new DocumentsError('No active session. Please sign in again.', 401)
  }
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method: init.method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    })
  } catch {
    throw new DocumentsError('No connection to the engine. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new DocumentsError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: string } | null)?.message ?? 'Request failed.'
    throw new DocumentsError(message, res.status)
  }
  return data
}

/** What one ingestion did, count by count. */
export interface IngestSummary {
  proposed: number
  duplicates: number
  needCertDate: number
  failed: number
  vetoed: number
  failedChunks: number
  /**
   * A27: how many suggested facts gave a password or access code and were
   * left out. Present only when the engine sent a count above zero, so every
   * other summary is exactly the object it always was.
   */
  withheld?: number
  /**
   * Kelvo build step 3: whether the uploaded file itself is being kept. Present
   * only when the engine keeps originals for this business; ABSENT (not false)
   * everywhere else, so a Foredesk summary is exactly the object it always was.
   *
   * `drawer` (Kelvo item 2) is where the file IS — present only when the engine
   * said. For a file already held it can differ from the drawer just chosen,
   * and describeOriginal says so.
   */
  original?: { kept: boolean; drawer?: Drawer | null }
}

function asCount(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0
}

export async function ingestDocument(
  token: string | null,
  input: { name: string; text: string; drawer?: Drawer },
): Promise<IngestSummary> {
  const { drawer, ...rest } = input
  const data = (await documentsRequest(token, '/api/admin/documents/ingest', {
    method: 'POST',
    // Foredesk sends exactly { name, text }, as it always has.
    body: drawer ? { ...rest, drawer } : rest,
  })) as Record<string, unknown>
  return {
    proposed: asCount(data.proposed),
    duplicates: asCount(data.duplicates),
    needCertDate: asCount(data.needCertDate),
    failed: asCount(data.failed),
    vetoed: asCount(data.vetoed),
    failedChunks: asCount(data.failedChunks),
    ...withheldFrom(data),
  }
}

/** A27: the withheld count, carried only when the engine sent one above zero. */
export function withheldFrom(data: Record<string, unknown>): { withheld?: number } {
  const n = asCount(data.withheld)
  return n > 0 ? { withheld: n } : {}
}

/**
 * UPLOAD A FILE (Build 2b).
 *
 * NOT documentsRequest, and that is the whole point of it being separate. That
 * helper JSON-encodes its body; a file sent through it would arrive as a base64
 * blob inside a JSON string, or worse, as `{}`. The file IS the request body
 * here, raw, with its own content type — the shape the engine expects, and the
 * one that never touches multipart (Workers' FormData corrupts binary through
 * UTF-8 coercion, a documented trap this design declines to enter rather than
 * work around).
 *
 * THE REASON CODE IS CARRIED, not flattened into the message. A scan and a
 * too-large file are different situations with different next steps, and the
 * form offers a different way forward for each. Matching on prose to tell them
 * apart is how a reworded sentence silently changes behaviour.
 */
export class DocumentUploadError extends DocumentsError {
  constructor(
    message: string,
    status: number | undefined,
    /** The engine's machine-readable reason, when it gave one. */
    readonly reason: string | null,
  ) {
    super(message, status)
    this.name = 'DocumentUploadError'
  }
}

export async function uploadDocument(
  token: string | null,
  file: {
    /** The owner's label for the document — what every fact will carry. */
    name: string
    /**
     * The file's REAL name on his computer, which carries the extension the
     * engine reads the type from. Sent separately since 28 Sep 2026: when the
     * label travelled alone, renaming the document refused an ordinary .txt.
     * Absent only for a caller that has no file name to give, in which case
     * the engine reads `name` as the filename, as it always did.
     */
    fileName?: string
    type: string
    bytes: ArrayBuffer
  },
  /** Kelvo only: the drawer to file a new document in. Absent on Foredesk. */
  drawer?: Drawer,
): Promise<IngestSummary> {
  if (!token) {
    throw new DocumentUploadError('No active session. Please sign in again.', 401, null)
  }

  let res: Response
  try {
    res = await fetch(
      // G73 (Build 13h): the names go in the request, never the URL. A file's
      // name is content, and URLs are seen by proxies, logs and browser tools.
      `${env.VITE_API_URL}/api/admin/documents/upload` +
        (drawer ? `?drawer=${encodeURIComponent(drawer)}` : ''),
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Document-Name': encodeURIComponent(file.name),
          ...(file.fileName !== undefined ? { 'X-File-Name': encodeURIComponent(file.fileName) } : {}),
          // The browser's own idea of the file's type. The engine treats it as a
          // claim and checks it against the bytes, which is why sending it
          // honestly costs nothing and helps the ordinary case.
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file.bytes,
      },
    )
  } catch {
    throw new DocumentUploadError(
      'No connection to the engine. Check your network and try again.',
      undefined,
      null,
    )
  }

  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new DocumentUploadError(
      'The engine returned an unreadable response.',
      res.status,
      null,
    )
  }

  if (!res.ok) {
    const body = data as { message?: string; reason?: string } | null
    throw new DocumentUploadError(
      body?.message ?? 'Could not read that file.',
      res.status,
      typeof body?.reason === 'string' ? body.reason : null,
    )
  }

  const d = data as Record<string, unknown>
  const doc = d.document as { kept?: unknown; drawer?: unknown } | null | undefined
  // The drawer is carried only when the engine sent a real one (or null for
  // unfiled). Anything else is dropped, never guessed at.
  const docDrawer =
    doc && 'drawer' in doc && (doc.drawer === null || isDrawer(doc.drawer))
      ? { drawer: doc.drawer as Drawer | null }
      : {}
  return {
    proposed: asCount(d.proposed),
    duplicates: asCount(d.duplicates),
    needCertDate: asCount(d.needCertDate),
    failed: asCount(d.failed),
    vetoed: asCount(d.vetoed),
    failedChunks: asCount(d.failedChunks),
    ...withheldFrom(d),
    ...(doc && typeof doc.kept === 'boolean' ? { original: { kept: doc.kept, ...docDrawer } } : {}),
  }
}

/**
 * The one sentence about the file itself, for an upload that reported one
 * (Kelvo). Null when the engine said nothing about it (Foredesk), so the caller
 * shows the ingest summary alone, exactly as before.
 *
 * Honest in both directions: a kept file is only openable by staff once a fact
 * from it is approved, and a file that yielded nothing is said NOT to be held —
 * which matters to an owner who has just uploaded something with a person's
 * name on it.
 */
export function describeOriginal(s: IngestSummary, chosen?: Drawer): string | null {
  if (!s.original) return null
  if (!s.original.kept) return 'Nothing in it became a fact, so the file itself was not kept.'
  const kept = 'The file itself is kept securely. Staff can open it once you approve a fact from it.'
  // Kelvo item 2. The same file uploaded again is the same document, and it
  // stays in the drawer it was already in. Said plainly, so the owner is not
  // left believing it went where he just picked.
  const where = s.original.drawer
  if (chosen !== undefined && where !== undefined && where !== chosen) {
    return where === null
      ? `You already had this file, and it is not in a drawer yet, so only you can see it. File it below.`
      : `You already had this file, filed in ${DRAWER_LABELS[where]}. It stays there; move it below if it belongs somewhere else.`
  }
  return kept
}

// ---------------------------------------------------------------------------
// DRAWERS (Kelvo item 2) — the words the owner sees for each drawer.
// ---------------------------------------------------------------------------

export { DRAWERS, DRAWER_LABELS, type Drawer }

/**
 * Who a drawer's contents reach, in the owner's words. Read from the same role
 * table the Worker enforces (workers/drawers.ts), never restated here, so the
 * sentence cannot promise something the engine does not do.
 */
export function whoSeesDrawer(drawer: Drawer | null): string {
  const office = drawer !== null && STAFF_DRAWERS.office.includes(drawer)
  const site = drawer !== null && STAFF_DRAWERS.site.includes(drawer)
  if (office && site) return 'You, the office and site staff'
  if (office) return 'You and the office'
  if (site) return 'You and site staff'
  return 'Only you'
}

// ---------------------------------------------------------------------------
// STORED ORIGINALS (Kelvo build step 3c) — the owner's list, and opening a file.
// ---------------------------------------------------------------------------

export interface StoredDocument {
  id: string
  name: string
  contentType: string
  byteSize: number
  uploadedAt: number
  factCount: number
  /** Approved and current: what deleting the file retires (A21). */
  currentFactCount: number
  /** Proposals awaiting review: what deleting the file rejects (A21). */
  pendingFactCount: number
  /** Kelvo item 2. Null is unfiled — only the owner sees what is in it. */
  drawer: Drawer | null
  /** G46: answers the owner wrote that offer this file. Deleting it leaves
   *  them standing, without the file. */
  attachedAnswerCount: number
  /** Build 12e: renewals lines that keep this file. */
  renewalCount: number
  /** Build 12e: when the nightly sweep removes it (epoch seconds); null while
   *  anything uses it. Worked out by the engine, by the sweep's own rule. */
  removalAt: number | null
}

function toStoredDocument(raw: unknown): StoredDocument | null {
  const r = (raw ?? {}) as Record<string, unknown>
  if (typeof r.id !== 'string' || typeof r.name !== 'string') return null
  return {
    id: r.id,
    name: r.name,
    contentType: typeof r.contentType === 'string' ? r.contentType : '',
    byteSize: asCount(r.byteSize),
    uploadedAt: asCount(r.uploadedAt),
    factCount: asCount(r.factCount),
    currentFactCount: asCount(r.currentFactCount),
    pendingFactCount: asCount(r.pendingFactCount),
    // Anything that is not a drawer reads as unfiled: the narrowest reading,
    // matching what the engine itself does with a file it cannot place.
    drawer: isDrawer(r.drawer) ? r.drawer : null,
    attachedAnswerCount: asCount(r.attachedAnswerCount),
    renewalCount: asCount(r.renewalCount),
    // A date the engine did not send is no date: the card then says nothing
    // about removal rather than inventing one.
    removalAt:
      typeof r.removalAt === 'number' && Number.isFinite(r.removalAt) && r.removalAt > 0
        ? Math.floor(r.removalAt)
        : null,
  }
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/**
 * WHAT USES A KEPT FILE, AND WHEN IT GOES (Build 12e). Until 7 Oct 2026 the
 * card said "N facts from it", counting withdrawn ones too, so a file nothing
 * used, and the sweep was about to remove, read as in use. Now the line says
 * what is true, and `removal` says when the engine's sweep will take it.
 */
export function describeStoredUse(
  d: Pick<
    StoredDocument,
    'factCount' | 'currentFactCount' | 'pendingFactCount' | 'attachedAnswerCount' | 'renewalCount' | 'removalAt'
  >,
  nowSeconds: number,
): { use: string; removal: string | null } {
  const withdrawn = Math.max(0, d.factCount - d.currentFactCount - d.pendingFactCount)
  const parts: string[] = []
  if (d.currentFactCount > 0) parts.push(`${plural(d.currentFactCount, 'fact', 'facts')} in use`)
  if (d.pendingFactCount > 0) parts.push(`${d.pendingFactCount} waiting for you`)
  if (d.attachedAnswerCount > 0) parts.push(`offered with ${plural(d.attachedAnswerCount, 'answer', 'answers')}`)
  if (d.renewalCount > 0) parts.push('on your renewals list')
  const inUse = parts.length > 0
  let use: string
  if (!inUse) {
    use = withdrawn > 0 ? `nothing uses it now (${withdrawn} withdrawn)` : 'nothing uses it'
  } else {
    if (withdrawn > 0) parts.push(`${withdrawn} withdrawn`)
    use = parts.join(' · ')
  }
  let removal: string | null = null
  if (!inUse && d.removalAt !== null) {
    removal =
      d.removalAt <= nowSeconds
        ? 'Due to be removed at the next nightly tidy-up, unless something uses it again.'
        : `Will be removed early on ${new Date(d.removalAt * 1000).toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            timeZone: 'Europe/London',
          })}, unless something uses it again.`
  }
  return { use, removal }
}

export async function fetchStoredDocuments(token: string | null): Promise<StoredDocument[]> {
  const data = (await documentsRequest(token, '/api/admin/documents', { method: 'GET' })) as {
    documents?: unknown
  }
  if (!Array.isArray(data.documents)) return []
  return data.documents.map(toStoredDocument).filter((d): d is StoredDocument => d !== null)
}

/**
 * What the owner is told before a file is deleted (A21). Deleting a file takes
 * its answers with it, so the confirm says exactly what stops: how many facts
 * stop being used in answers, and how many waiting for review are dropped.
 * Counts come from the list the screen already holds; the engine does the
 * same whatever the screen said.
 */
export function deleteFileWarning(
  d: Pick<StoredDocument, 'currentFactCount' | 'pendingFactCount'> & Partial<Pick<StoredDocument, 'attachedAnswerCount'>>,
): string {
  const parts = ['Delete this file for good? Staff will no longer be able to open it.']
  if (d.currentFactCount > 0) {
    parts.push(
      d.currentFactCount === 1
        ? 'The 1 fact drawn from it will stop being used in answers.'
        : `The ${d.currentFactCount} facts drawn from it will stop being used in answers.`,
    )
  }
  if (d.pendingFactCount > 0) {
    parts.push(
      d.pendingFactCount === 1
        ? 'The 1 fact from it waiting for your review will be dropped.'
        : `The ${d.pendingFactCount} facts from it waiting for your review will be dropped.`,
    )
  }
  const attached = attachedAnswersWarning(d.attachedAnswerCount ?? 0)
  if (attached) parts.push(attached)
  parts.push('This cannot be undone.')
  return parts.join(' ')
}

export async function deleteStoredDocument(token: string | null, id: string): Promise<void> {
  await documentsRequest(token, `/api/admin/documents/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/**
 * Move a kept file to another drawer (null unfiles it). Every answer drawn
 * from it moves with it. A 409 means it was moved a moment ago elsewhere; the
 * engine's own sentence says to reload, and the screen does.
 */
export async function moveStoredDocument(
  token: string | null,
  id: string,
  drawer: Drawer | null,
): Promise<{ drawer: Drawer | null; moved: boolean }> {
  const data = (await documentsRequest(token, `/api/admin/documents/${encodeURIComponent(id)}/drawer`, {
    method: 'POST',
    body: { drawer },
  })) as { drawer?: unknown; moved?: unknown }
  return {
    drawer: isDrawer(data.drawer) ? data.drawer : null,
    moved: data.moved === true,
  }
}

/**
 * Fetch a stored original as a Blob, signed in. Never a plain link: the file
 * sits behind a session, so the browser cannot be pointed at it — the request
 * has to carry the token, and a URL a person could copy would carry nothing.
 *
 * `who` picks the door: the owner's admin route, or the staff route (which the
 * Worker only opens for a file behind a live answer). Every refusal the Worker
 * gives is a 404 by design, so that is worded as "no longer available".
 */
export async function fetchDocumentFile(
  token: string | null,
  id: string,
  who: 'owner' | 'staff',
): Promise<Blob> {
  if (!token) throw new DocumentsError('Your sign-in has expired. Please sign in again.', 401)
  const path =
    who === 'owner'
      ? `/api/admin/documents/${encodeURIComponent(id)}/file`
      : `/api/staff/documents/${encodeURIComponent(id)}/file`
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch {
    throw new DocumentsError('No connection. Check your signal and try again.')
  }
  if (res.status === 401) throw new DocumentsError('Your sign-in has expired. Please sign in again.', 401)
  if (res.status === 404) throw new DocumentsError('That document is no longer available.', 404)
  if (!res.ok) throw new DocumentsError('Could not open that document. Please try again.', res.status)
  return res.blob()
}

/** "1.2 MB", "340 KB" — for the owner's list. */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1000))} KB`
}

/**
 * One entry per document for the staff chat's sources: several facts from the
 * same file are one thing to open, not several. Order is the order of first
 * appearance, which is the ranking the engine returned.
 */
export function openableDocuments(sources: Source[]): { documentId: string; name: string }[] {
  const seen = new Set<string>()
  const out: { documentId: string; name: string }[] = []
  for (const s of sources) {
    if (typeof s.documentId !== 'string' || s.documentId === '' || seen.has(s.documentId)) continue
    seen.add(s.documentId)
    const name = typeof s.documentName === 'string' && s.documentName ? s.documentName : s.page
    out.push({ documentId: s.documentId, name })
  }
  return out
}

/**
 * Which refusals the owner can do something about HERE, by pasting instead.
 *
 * A scan is the case this exists for: the file is fine, we simply cannot read it
 * honestly, and the useful response is "paste the text in and we'll use that" —
 * not an error message that reads like the file was wrong. A file that is too
 * long is the same shape of problem with a different remedy (split it), and the
 * engine's own message already says so.
 */
export function uploadRefusalIsRecoverable(reason: string | null): boolean {
  // A scan, and (batch 2d) a note too short to be a document: both are files
  // we can see but will not read, and in both the text pasted in is just as
  // good. Every other refusal is about the file itself, where pasting its
  // text would not be the fix.
  return reason === 'unreadable_scan' || reason === 'too_short'
}

/**
 * The types the picker offers, taken from the SAME list the engine enforces.
 * Two hand-maintained lists is how a picker comes to offer a format the engine
 * refuses — the owner chooses a file, waits, and is told no for a reason he had
 * no way to anticipate.
 */
export { UPLOAD_ACCEPT_ATTRIBUTE, UPLOAD_LIMITS } from '../../workers/document-read'

/**
 * The result, in the owner's words. Every count that is zero says nothing —
 * the summary reads as a sentence about what happened, not a report card. The
 * two honesty lines matter most: vetoed candidates and failed chunks are what
 * distinguish "your document held twelve facts" from "we could only read part
 * of it", and hiding either would present a short list as a complete one.
 */
export function describeIngest(s: IngestSummary): string {
  const parts: string[] = []
  if (s.proposed > 0) {
    // What this read FOUND, not what is waiting now: a count of the past stays
    // true while he works down the list. The sentence itself is taken off the
    // screen once the list is empty (src/lib/intake-summary.ts).
    parts.push(
      `Found ${s.proposed} new ${s.proposed === 1 ? 'fact' : 'facts'} for you to check below.`,
    )
  } else {
    parts.push('Nothing new to add from this one.')
  }
  if (s.duplicates > 0) {
    parts.push(`${s.duplicates} already in your knowledge base.`)
  }
  if (s.needCertDate > 0) {
    parts.push(
      `${s.needCertDate} ${s.needCertDate === 1 ? 'looks' : 'look'} certificate-related — add ${s.needCertDate === 1 ? 'it' : 'those'} by hand with the certificate's expiry date.`,
    )
  }
  if (s.vetoed > 0) {
    parts.push(
      `${s.vetoed} suggested ${s.vetoed === 1 ? 'fact' : 'facts'} couldn't be matched to the document word for word, so ${s.vetoed === 1 ? 'it was' : 'they were'} left out.`,
    )
  }
  if (s.failedChunks > 0) {
    parts.push('Part of the document could not be read, so the list may be short — try that part again.')
  }
  if (s.withheld !== undefined && s.withheld > 0) {
    // A27: said, never silent. The owner learns the rule from the sentence.
    parts.push(
      `${s.withheld} looked like a password or access code, so ${s.withheld === 1 ? 'it was' : 'they were'} left out. Codes are given to people, not kept here.`,
    )
  }
  return parts.join(' ')
}

/** One fact awaiting the owner's signature. */
export interface PendingFact {
  id: string
  content: string
  sourceType: string
  sourceDoc: string | null
  volatilityClass: VolatilityClass
  extractionConfidence: number | null
  extractedAt: number
  /**
   * Kelvo item 2: the drawer this fact is in, so the card he signs says who
   * will be told it. null is UNFILED (only him). undefined means the engine
   * sent no drawer at all — Foredesk, which has none — and the card says
   * nothing about drawers.
   */
  drawer?: Drawer | null
}

export async function fetchPendingFacts(token: string | null): Promise<PendingFact[]> {
  const data = await documentsRequest(token, '/api/admin/facts/pending', { method: 'GET' })
  const raw = (data as { facts?: unknown } | null)?.facts
  if (!Array.isArray(raw)) return []
  const out: PendingFact[] = []
  for (const item of raw) {
    const r = (item ?? {}) as Record<string, unknown>
    // A row with no id or no content is dropped, not repaired — an Approve
    // button over an invisible fact invites signing something unseen, the same
    // rule the re-confirmation queue applies.
    if (typeof r.id !== 'string' || !r.id) continue
    if (typeof r.content !== 'string' || !r.content) continue
    out.push({
      id: r.id,
      content: r.content,
      sourceType: typeof r.sourceType === 'string' ? r.sourceType : 'document',
      sourceDoc: typeof r.sourceDoc === 'string' ? r.sourceDoc : null,
      volatilityClass: isVolatilityClass(r.volatilityClass) ? r.volatilityClass : 'short',
      extractionConfidence:
        typeof r.extractionConfidence === 'number' && Number.isFinite(r.extractionConfidence)
          ? r.extractionConfidence
          : null,
      extractedAt: asCount(r.extractedAt),
      // Present only when the engine sent the key. A value that is not a
      // drawer reads as UNFILED — the answer that can only understate who
      // will be told, never overstate it.
      ...('drawer' in r ? { drawer: isDrawer(r.drawer) ? r.drawer : null } : {}),
    })
  }
  return out
}

/**
 * The line a pending fact's card shows about its drawer: who will be told it
 * once he approves it, in the same words the drawer picker and the kept-files
 * list use. Unfiled is flagged, because approving an unfiled fact makes it
 * live for him alone — correct, but never something he should do unawares.
 */
export function pendingDrawerLine(drawer: Drawer | null): { text: string; unfiled: boolean } {
  if (drawer === null) {
    return { text: 'Not in a drawer — once approved, only you will see it', unfiled: true }
  }
  const who = whoSeesDrawer(drawer)
  return {
    text: `${DRAWER_LABELS[drawer]} · once approved: ${who.charAt(0).toLowerCase()}${who.slice(1)}`,
    unfiled: false,
  }
}

function isVolatilityClass(v: unknown): v is VolatilityClass {
  return v === 'short' || v === 'medium' || v === 'long' || v === 'cert_tied'
}

/** Sign a pending fact, optionally correcting its volatility. A correction to
 *  cert_tied requires the certificate's expiry (epoch seconds — build it with
 *  certDateToEpoch, never new Date().getTime()). */
export async function approvePendingFact(
  token: string | null,
  id: string,
  override?: { volatilityClass: VolatilityClass; certExpiresAt?: number },
): Promise<{ embedded: boolean }> {
  const data = (await documentsRequest(token, `/api/admin/facts/${id}/approve`, {
    method: 'POST',
    body: override,
  })) as Record<string, unknown>
  return { embedded: data.embedded === true }
}

export async function rejectPendingFact(token: string | null, id: string): Promise<void> {
  await documentsRequest(token, `/api/admin/facts/${id}/reject`, { method: 'POST' })
}

/* ---------------------------------------------------------------------------
 * The LIVE corpus (Build 2c) — what the assistant currently knows.
 *
 * Distinct from PendingFact in more than shape: a PendingFact is a proposal the
 * owner has not signed, a LiveFact is one he has. They are never mixed in one
 * list, because the only question that matters on this screen — "is my
 * assistant telling people this?" — has opposite answers for the two.
 * ------------------------------------------------------------------------- */

/** One fact the owner has approved and not withdrawn. */
export interface LiveFact {
  id: string
  content: string
  sourceType: string
  sourceDoc: string | null
  volatilityClass: VolatilityClass
  approvedAt: number
  lastConfirmed: number | null
  expiresAt: number | null
  /**
   * FALSE means approved but carrying no vector, so no customer can be told it.
   * Almost always an embed that failed and was never re-fed. Surfaced rather
   * than hidden: it is the owner's signed fact, and it exists on no other
   * screen in the product.
   */
  answering: boolean
  /**
   * When the owner published this to his public page, or null if he has not.
   *
   * A TIMESTAMP RATHER THAN A BOOLEAN, because "since when" is the honest
   * question about something on the open web, and the card can say it. It also
   * defaults to the SAFE direction on malformed input: unknown becomes null,
   * i.e. "not published", so a parsing accident can never draw a Published badge
   * over a fact no stranger can actually read.
   */
  publishedAt: number | null
  /**
   * Kelvo part 2c: the drawer this fact is filed in; null is unfiled (only the
   * owner is told it). undefined — no key from the engine — is Foredesk, where
   * nothing is filed and the card says nothing about drawers.
   */
  drawer?: Drawer | null
  /**
   * Kelvo part 2c: the kept file behind this fact, if any. Such a fact moves
   * only with its file. `kept` is false only if a current fact's file has been
   * deleted, which A21 (batch 2aa) made impossible: deleting a file retires
   * its facts. Kept as a second lock, so such a fact is never offered a move.
   */
  fromDocument?: { name: string; kept: boolean } | null
  /**
   * Build 10b: the recording behind this answer, on a pod with voice answers.
   * null means none (or deleted); undefined means the engine sent no key.
   */
  voiceNote?: FactRecording | null
  /**
   * G46: the kept file this answer offers staff to open, on a pod that keeps
   * files; null means none. undefined — no key — is a pod without kept files,
   * or a fact read from a file (which has its own).
   */
  attachment?: FactAttachment | null
}

export interface LiveCorpus {
  facts: LiveFact[]
  /** TRUE when the corpus is larger than the page returned. Reported, never
   *  swallowed: a silent cap would tell the owner he had seen everything. */
  truncated: boolean
}

export async function fetchLiveFacts(token: string | null): Promise<LiveCorpus> {
  const data = await documentsRequest(token, '/api/admin/facts', { method: 'GET' })
  const body = (data ?? {}) as Record<string, unknown>
  const raw = body.facts
  if (!Array.isArray(raw)) return { facts: [], truncated: false }
  const facts: LiveFact[] = []
  for (const item of raw) {
    const r = (item ?? {}) as Record<string, unknown>
    // Same rule as the review queue: a row with no id or no content is dropped,
    // not repaired. A Retire button over an invisible fact invites withdrawing
    // something unseen.
    if (typeof r.id !== 'string' || !r.id) continue
    if (typeof r.content !== 'string' || !r.content) continue
    facts.push({
      id: r.id,
      content: r.content,
      sourceType: typeof r.sourceType === 'string' ? r.sourceType : 'typed',
      sourceDoc: typeof r.sourceDoc === 'string' ? r.sourceDoc : null,
      volatilityClass: isVolatilityClass(r.volatilityClass) ? r.volatilityClass : 'long',
      approvedAt: asCount(r.approvedAt),
      lastConfirmed: typeof r.lastConfirmed === 'number' ? r.lastConfirmed : null,
      expiresAt: typeof r.expiresAt === 'number' ? r.expiresAt : null,
      // Defaults to TRUE when the field is missing or malformed, deliberately.
      // The two ways to be wrong are not equal: claiming a live fact is silent
      // sends the owner chasing a problem that does not exist, while the
      // opposite understates a flag he can check for himself.
      answering: r.answering !== false,
      // Defaults to NULL — unpublished — when missing or malformed, and the
      // asymmetry with `answering` above is deliberate. Both defaults point at
      // the outcome that is safe to be wrong about: claiming a fact is NOT on
      // the public page understates something the owner can check, while
      // claiming it IS would tell him a stranger can read something that may
      // never have been published.
      publishedAt: typeof r.publishedAt === 'number' && r.publishedAt > 0 ? r.publishedAt : null,
      // Kelvo part 2c. Present only when the engine sent the key. An unknown
      // drawer reads as unfiled (understates who is told). A malformed file
      // link reads as NO file, but a present-and-malformed one reads as a
      // file that is not kept: the screen then offers no move at all, which
      // is the direction that cannot send an answer somewhere by mistake.
      ...('drawer' in r ? { drawer: isDrawer(r.drawer) ? r.drawer : null } : {}),
      ...('fromDocument' in r ? { fromDocument: toFromDocument(r.fromDocument) } : {}),
      ...('voiceNote' in r ? { voiceNote: toFactRecording(r.voiceNote) } : {}),
      ...('attachment' in r ? { attachment: toFactAttachment(r.attachment) } : {}),
    })
  }
  return { facts, truncated: body.truncated === true }
}

function toFromDocument(raw: unknown): { name: string; kept: boolean } | null {
  if (raw === null || raw === undefined) return null
  const r = (typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return {
    name: typeof r.name === 'string' && r.name ? r.name : 'a kept file',
    kept: r.kept === true,
  }
}

/**
 * Whether the owner may move this fact on its own. Only a filed-or-unfiled
 * fact on a drawers pod with NO file behind it: a document's facts move with
 * the document (on the Documents page). A fact whose file was deleted is
 * retired with it (A21), so it is never listed; were one ever listed, it has a
 * file behind it and is not offered a move. The engine refuses the same cases;
 * this keeps the screen from offering a move it would refuse.
 */
export function canMoveLiveFact(fact: LiveFact): boolean {
  return fact.drawer !== undefined && (fact.fromDocument ?? null) === null
}

/**
 * Move one approved fact to another drawer (null unfiles it). A 409 means
 * either it moved a moment ago elsewhere or it has a file behind it; the
 * engine's own sentence says which, and the screen reloads.
 */
export async function moveLiveFact(
  token: string | null,
  id: string,
  drawer: Drawer | null,
): Promise<{ drawer: Drawer | null; moved: boolean }> {
  const data = (await documentsRequest(token, `/api/admin/facts/${encodeURIComponent(id)}/drawer`, {
    method: 'POST',
    body: { drawer },
  })) as { drawer?: unknown; moved?: unknown }
  return {
    drawer: isDrawer(data.drawer) ? data.drawer : null,
    moved: data.moved === true,
  }
}

/**
 * The line a live fact's card shows about its drawer, in the same words the
 * drawer picker and the kept-files list use. Unfiled is flagged.
 */
export function liveFactDrawerLine(drawer: Drawer | null): { text: string; unfiled: boolean } {
  if (drawer === null) return { text: 'Not in a drawer \u2014 only you are told this', unfiled: true }
  return { text: `${DRAWER_LABELS[drawer]} \u00b7 ${whoSeesDrawer(drawer)}`, unfiled: false }
}

/**
 * The line an Answered card shows (Kelvo part 2c): who is told this answer now.
 * Taken out is said plainly; unfiled is flagged, because an answer only the
 * owner can be told is almost never what he meant for a question staff asked.
 */
export function answeredDrawerLine(filed: AnsweredFiling | null): { text: string; flagged: boolean } {
  // No fact stands behind it: saved before answers became facts, or on a pod
  // still on the older answer path. Whether anyone is told it is not known
  // from here, so the line claims nothing either way — and is flagged.
  if (filed === null) return { text: 'No drawer on record for this answer', flagged: true }
  if (!filed.live) return { text: 'Taken out \u2014 nobody is told this answer any more', flagged: false }
  if (filed.drawer === null) {
    return { text: 'Not in a drawer \u2014 only you are told this answer', flagged: true }
  }
  return { text: `${DRAWER_LABELS[filed.drawer]} \u00b7 ${whoSeesDrawer(filed.drawer)}`, flagged: false }
}

/**
 * Put a fact on the business's own public page, or take it off again (Build 5).
 *
 * TWO FUNCTIONS, NOT ONE TAKING A BOOLEAN, mirroring the two endpoints. A single
 * `setPublished(id, next)` would compute `next` from whatever the component last
 * rendered, so a stale card could publish something the owner meant to withdraw.
 * Naming the act at the call site removes that class of mistake entirely.
 */
export async function publishLiveFact(token: string | null, id: string): Promise<void> {
  await documentsRequest(token, `/api/admin/facts/${id}/publish`, { method: 'POST' })
}

export async function unpublishLiveFact(token: string | null, id: string): Promise<void> {
  await documentsRequest(token, `/api/admin/facts/${id}/unpublish`, { method: 'POST' })
}

/** Withdraw an approved fact. The content is kept; the vector is not. */
export async function retireLiveFact(token: string | null, id: string): Promise<void> {
  await documentsRequest(token, `/api/admin/facts/${id}/retire`, { method: 'POST' })
}

/**
 * Replace an approved fact with corrected wording.
 *
 * Distinct from retiring and re-adding, which is what an owner would otherwise
 * have to do: this keeps the chain, so the old row records what customers used
 * to be told and points at what replaced it. Returns `embedded` honestly — false
 * means the replacement is signed but not yet answering, which the card shows.
 */
export async function replaceLiveFact(
  token: string | null,
  id: string,
  update: { content: string; volatilityClass?: VolatilityClass; certExpiresAt?: number },
): Promise<{ factId: string; embedded: boolean }> {
  const data = (await documentsRequest(token, `/api/admin/facts/${id}/replace`, {
    method: 'POST',
    body: update,
  })) as Record<string, unknown>
  return {
    factId: typeof data.factId === 'string' ? data.factId : '',
    embedded: data.embedded === true,
  }
}

/* ---------------------------------------------------------------------------
 * Voice capture (Build 1) — the PUBLIC, token-authenticated pair.
 *
 * These deliberately do NOT go through authedRequest: there is no Clerk session
 * on the record page. The owner opens it from an SMS on his phone with no
 * login, so the link token IS the authentication. It travels in the path, never
 * in a header, because the whole link has to survive being tapped from a text
 * message.
 * ------------------------------------------------------------------------- */

export interface VoiceCaptureState {
  ok: true
  captureId: string
}

/** Why a record link will not open. Drives the message the owner reads. */
export type VoiceLinkProblem = 'not_found' | 'expired' | 'already_used' | 'unavailable'

export class VoiceLinkError extends Error {
  constructor(readonly problem: VoiceLinkProblem, message: string) {
    super(message)
    this.name = 'VoiceLinkError'
  }
}

/** Check a link before showing a record button. */
export async function checkVoiceLink(token: string): Promise<VoiceCaptureState> {
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}/api/voice/${encodeURIComponent(token)}`)
  } catch {
    throw new VoiceLinkError('unavailable', 'Could not reach the server. Check your signal and try again.')
  }
  if (res.ok) return (await res.json()) as VoiceCaptureState

  const body = (await res.json().catch(() => null)) as { reason?: string } | null
  const reason = body?.reason
  if (reason === 'expired') {
    throw new VoiceLinkError('expired', 'This link has expired. Ask for a new one and it will work straight away.')
  }
  if (reason === 'already_used') {
    throw new VoiceLinkError('already_used', 'You have already answered this one. It is waiting for you to approve it.')
  }
  throw new VoiceLinkError('not_found', 'This link is not valid. It may have been replaced by a newer one.')
}

export interface VoiceUploadResult {
  transcript: string
  confidence: number
}

/**
 * Upload a recording. Distinguishes the two failure kinds the engine reports,
 * because they need opposite messages: 422 means try again on this same link,
 * 502 means something is broken and trying again will not fix it.
 */
export async function uploadRecording(
  token: string,
  audio: Blob,
): Promise<{ ok: true; result: VoiceUploadResult } | { ok: false; canRetry: boolean; message: string }> {
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}/api/voice/${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'Content-Type': audio.type || 'audio/webm' },
      body: audio,
    })
  } catch {
    return { ok: false, canRetry: true, message: 'Could not reach the server. Check your signal and try again.' }
  }

  if (res.ok) {
    const body = (await res.json()) as VoiceUploadResult
    return { ok: true, result: body }
  }

  const body = (await res.json().catch(() => null)) as
    | { message?: string; canRetry?: boolean }
    | null
  return {
    ok: false,
    canRetry: body?.canRetry === true,
    message: body?.message ?? 'That recording did not come through.',
  }
}

/* ---------------------------------------------------------------------------
 * Voice review + approval (Build 1) — AUTHENTICATED, for the dashboard.
 * ------------------------------------------------------------------------- */

/**
 * The engine's four volatility classes, RE-EXPORTED from the engine's own
 * dependency-free module rather than restated here.
 *
 * It was restated, until this batch. A second copy of a vocabulary is a copy
 * that can drift: add a class in the engine and the dashboard silently cannot
 * offer it; reword a label and the option the owner picked says one thing while
 * the re-confirmation message he later receives says another. Same anti-drift
 * rule as the capture sentinel — one definition, imported by both sides.
 */
export type { VolatilityClass }

export interface VoiceDraft {
  id: string
  questionId: string | null
  transcript: string
  confidence: number | null
  createdAt: number
  /** The engine's proposal. Shown, not applied silently. */
  proposedVolatility: VolatilityClass
  /** Plain English for the owner. The enum value never reaches his screen. */
  volatilityLabel: string
  volatilityReason: string
  /** True when nothing matched and the engine fell back rather than recognised. */
  volatilityIsGuess: boolean
}

/** Fetch the drafts awaiting review. */
export async function fetchVoiceDrafts(token: string | null): Promise<VoiceDraft[]> {
  const data = await authedRequest(token, '/api/admin/voice/review', { method: 'GET' })
  return ((data as { captures?: VoiceDraft[] }).captures ?? []).filter((d) => d.transcript)
}

/**
 * Approve a draft: mints the fact, closes the gap, makes it answerable.
 *
 * volatilityClass is REQUIRED and has no client-side default on purpose. The
 * dashboard must send what the owner actually SAW on screen, so a proposal can
 * never be applied without having been displayed to him.
 */
export async function approveVoiceDraft(
  token: string | null,
  id: string,
  input: { content: string; volatilityClass: VolatilityClass; certExpiresAt?: number },
): Promise<void> {
  await authedRequest(token, `/api/admin/voice/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/**
 * The four choices, worded for a tradesperson rather than a data modeller —
 * re-exported from the engine so the wording cannot diverge from what the
 * engine's own messages say. `proposeVolatility` comes with it: the answer
 * screen needs the proposal BEFORE saving, which is the whole point of showing
 * it rather than applying it silently.
 */
export { VOLATILITY_CHOICES, proposeVolatility } from '../../workers/volatility'
export type { VolatilityProposal } from '../../workers/volatility'

// ---------------------------------------------------------------------------
// Build 8 — case files. One customer's history across every channel, and the
// outcome the owner records against it.
//
// SAME AUTHED PATTERN as the leads client above: the caller supplies the Clerk
// token, we send Bearer, and the engine resolves the tenant from the verified
// session. These calls never send a tenant — a tenant on the wire would be a
// value the client controls, and the whole isolation model rests on it being
// one the server derives.
//
// EVERY COERCION LIVES IN src/lib/case-file-shape.ts, not here. That module is
// pure and dependency-free so it can be tested under the Workers tsconfig
// project without adding `vite/client` to it — see its header for why that
// matters. What is left in this file is the request wrapper, which is a third
// repeat of an already-proven pattern.
// ---------------------------------------------------------------------------

/**
 * Typed error for the case-files client.
 *
 * `alreadyClosedOutcome` is set only on the 409 from the close endpoint, where
 * the engine reports the outcome the file already carries. Surfacing it is the
 * difference between telling an owner "already closed as quoted" and telling
 * them something went wrong — and the second reads as data loss when nothing
 * was lost, because a 409 here is almost always a stale tab.
 */
export class CaseFilesError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly alreadyClosedOutcome?: CaseFileOutcome,
  ) {
    super(message)
    this.name = 'CaseFilesError'
  }
}

async function caseFilesRequest(
  token: string | null,
  path: string,
  init: { method: string; headers?: Record<string, string>; body?: string },
): Promise<unknown> {
  if (!token) {
    throw new CaseFilesError('No active session. Please sign in again.', 401)
  }
  let res: Response
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method: init.method,
      headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` },
      body: init.body,
    })
  } catch {
    throw new CaseFilesError('No connection to the engine. Check your network and try again.')
  }
  let data: unknown
  try {
    data = await res.json()
  } catch {
    throw new CaseFilesError('The engine returned an unreadable response.', res.status)
  }
  if (!res.ok) {
    const message = (data as { message?: string } | null)?.message ?? 'Request failed.'
    throw new CaseFilesError(message, res.status, parseAlreadyClosedOutcome(data))
  }
  return data
}

/** The signed-in supplier's case files, newest first. `status` optional. */
export async function fetchCaseFiles(
  token: string | null,
  status?: CaseFileStatus,
): Promise<{ caseFiles: CaseFileSummary[]; truncated: boolean }> {
  const path = status
    ? `/api/admin/case-files?status=${encodeURIComponent(status)}`
    : '/api/admin/case-files'
  const parsed = parseCaseFileList(await caseFilesRequest(token, path, { method: 'GET' }))
  if (parsed === null) {
    throw new CaseFilesError('Unexpected response from the engine.')
  }
  return parsed
}

/** One case file with its resolved timeline. Throws 404 if it is not theirs. */
export async function fetchCaseFile(token: string | null, id: string): Promise<CaseFileDetail> {
  const data = await caseFilesRequest(token, `/api/admin/case-files/${encodeURIComponent(id)}`, {
    method: 'GET',
  })
  const parsed = parseCaseFileDetail(data)
  if (parsed === null) {
    throw new CaseFilesError('Unexpected response from the engine.')
  }
  return parsed
}

/**
 * Close a case file with its outcome. PERMANENT — there is no reopening, and
 * the engine enforces that rather than the UI.
 */
export async function closeCaseFile(
  token: string | null,
  id: string,
  outcome: CaseFileOutcome,
): Promise<void> {
  await caseFilesRequest(token, `/api/admin/case-files/${encodeURIComponent(id)}/close`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ outcome }),
  })
}
